import * as React from "react";
import {
  Box,
  Typography,
  Button,
  Checkbox,
  Stack,
} from "@mui/material";
import { useI18n } from "../i18n/I18nProvider";
import { supabase } from "../logic/client";


export default function UserRolesSetup(props: {
  handlePageChange?: (p: string) => void;
}) {
  const { lang } = useI18n();
  const L = React.useCallback(
    (en: string, de: string) => (lang === "de" ? de : en),
    [lang]
  );

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [roles, setRoles] = React.useState<any[]>([]);
  const [selected, setSelected] = React.useState<string[]>([]);

  /* ---------------- LOAD ROLES ---------------- */
  React.useEffect(() => {
    const loadRoles = async () => {
      const { data, error } = await supabase
        .from("user_roles_catalog")
        .select("*")
        .order("created_at");

      if (error) {
        console.error(error);
        return;
      }

      setRoles(data || []);
    };

    loadRoles();
  }, []);

  /* ---------------- TOGGLE ---------------- */
  const toggleRole = (id: string) => {
    setSelected((prev) =>
      prev.includes(id)
        ? prev.filter((r) => r !== id)
        : [...prev, id]
    );
  };

  /* ---------------- SUBMIT ---------------- */
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) throw new Error("Not authenticated");

      const { data: employee } = await supabase
        .from("employees")
        .select("id")
        .eq("auth_user_id", user.id)
        .single();

      if (!employee) throw new Error("Employee not found");

      await supabase
        .from("employee_roles")
        .delete()
        .eq("employee_id", employee.id);

      if (selected.length > 0) {
        const inserts = selected.map((role_id) => ({
          employee_id: employee.id,
          role_id,
        }));

        const { error } = await supabase
          .from("employee_roles")
          .insert(inserts);

        if (error) throw error;
      }

      props.handlePageChange?.("JourneyPillars");
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: { xs: 4, md: 6 },
      }}
    >
      {/* BACKGROUND */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundImage: "url('/background.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          zIndex: 0,
        }}
      />

      {/* OVERLAY */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(circle at center, rgba(0,0,0,0.5), rgba(0,0,0,0.9))",
          zIndex: 1,
        }}
      />

      {/* CARD */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          maxWidth: 800,
          borderRadius: 3,
          p: { xs: 3, md: 5 },
          backdropFilter: "blur(18px)",
          background: "rgba(0,0,0,0.75)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* TITLE */}
        <Typography
          sx={{ color: "white", mb: 2, fontWeight: 600, fontSize: { xs: "1.6rem", sm: "2rem", md: "2.125rem" }, lineHeight: 1.2 }}
        >
          {L("Which roles do you have?", "Welche Rollen hast Du?")}
        </Typography>

        {/* SUBTEXT */}
        <Typography
          sx={{
            color: "rgba(255,255,255,0.7)",
            mb: 4,
            fontSize: "1rem",
          }}
        >
          {L(
            "Multiple selections possible.",
            "Mehrfachauswahl möglich."
          )}
        </Typography>

        {/* CHECKBOX LIST */}
        <Stack spacing={2}>
          {roles.map((role) => {
            const isSelected = selected.includes(role.id);

            return (
              <Box
                key={role.id}
                onClick={() => toggleRole(role.id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: 2,
                  py: 2,
                  borderRadius: 2,
                  cursor: "pointer",
                  transition: "0.2s",

                  background: isSelected
                    ? "rgba(200,30,30,0.2)"
                    : "rgba(255,255,255,0.05)",

                  border: isSelected
                    ? "1px solid #c81e1e"
                    : "1px solid rgba(255,255,255,0.08)",

                  "&:hover": {
                    background: "rgba(255,255,255,0.1)",
                  },
                }}
              >
                <Checkbox
                  checked={isSelected}
                  sx={{
                    color: "white",
                    "&.Mui-checked": {
                      color: "#c81e1e",
                    },
                  }}
                />

                <Typography
                  sx={{
                    color: "white",
                    fontSize: "1.1rem",
                  }}
                >
                  {lang === "de"
                    ? role.label_de
                    : role.label_en}
                </Typography>
              </Box>
            );
          })}
        </Stack>

        {/* BUTTON */}
        <Box sx={{ mt: 5 }}>
          <Button
            fullWidth
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              py: 2,
              fontSize: "1.2rem",
              background: "#c81e1e",
              color: "white",
              fontWeight: 700,
              borderRadius: 1.5,
              "&:hover": {
                background: "#a81818",
              },
            }}
          >
            {L("SAVE & CONTINUE", "SPEICHER & WEITER")}
          </Button>
        </Box>

        {/* ERROR */}
        {error && (
          <Typography
            sx={{
              mt: 2,
              color: "#ff4d4f",
              textAlign: "center",
            }}
          >
            {error}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
